/* eslint-disable */
import createLinkButton from "~/ui/linkbutton";
import { fragment, icon, node } from "~/util/dom";
import { createDialogButton } from "~/ui/dialogbutton";
import { InputSuggestMapClick } from "~/ui/inputsuggestmapclick";
import { IUserInitialPosition } from "~/state/user/interfaces";
import { EPopOverFlow } from "~/ui/popover";
import ContextMenu from "~/ui/contextmenu";
import ContextMenuItem from "~/ui/contextmenuitem";
import createSDSIcon from "~/ui/icon";
import { EDIT_FILLED_24, POI_FILLED_24, TRASH_FILLED_24 } from "@sznds/icons";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { store } from "~/state/store";
import { setCoordinates } from "~/state/map/state";
import { createHeader } from "~/profile/utils/header";

interface ISettingsInitialPositionProps {
	onChange: (poi: IUserInitialPosition) => Promise<void>;
	mapCoords: IUserInitialPosition | null;
}

function sameCoords(positionA: IUserInitialPosition, positionB: IUserInitialPosition): boolean {
	// pokud jsou obe null nebo obe reference na stejny objekt, jsou stejne
	if (positionA === positionB) {
		return true;
	}

	// jelikoz vime, ze neplati, ze by obe byly null, tak pokud jedna null je, tak nejsou stejne
	if (positionA === null || positionB === null) {
		return false;
	}

	// zbytek pripadu tedy mame dva objekty stejneho typu a musime porovnat hodnoty vlastnosti (title ne, ten se muze teoreticky zmenit a porad to podle source a id bude stejny bod)
	return (
		positionA.zoom === positionB.zoom
		&& positionA.source === positionB.source
		&& positionA.source_id === positionB.source_id
		&& positionA.mark.lat === positionB.mark.lat
		&& positionA.mark.lon === positionB.mark.lon
	);
}

export default class SettingsInitialPosition {
	#container: HTMLDivElement;

	#titlePara: HTMLParagraphElement;

	#form: HTMLFormElement;

	#input: HTMLInputElement;

	#suggest: InputSuggestMapClick;

	#props: ISettingsInitialPositionProps;

	#contextMenu: ContextMenu;

	#newPosition: IUserInitialPosition;

	constructor(props: ISettingsInitialPositionProps) {
		this.#newPosition = props.mapCoords;
		this.#props = props;
		this.#build();
		this.mapCoords = props.mapCoords;
	}

	destroy() {
		this.#container.innerHTML = "";
		this.#suggest.destroy();
	}

	get container() {
		return this.#container;
	}

	set mapCoords(coords: IUserInitialPosition | null) {
		this.#props.mapCoords = coords && Object.keys(coords).length ? coords : null;

		this.#titlePara.innerText = this.#props.mapCoords ? this.#props.mapCoords.title : _("profile.settings.position.empty");
		this.#container.classList[this.#props.mapCoords ? "remove" : "add"]("settings-initial-position--empty");
		this.#input.value = this.#props.mapCoords ? this.#props.mapCoords.title : "";
	}

	set loading(loading: boolean) {
		this.#container.classList[loading ? "add" : "remove"]("settings-initial-position--loading");
	}

	#build() {
		this.#container = node("div", { className: "settings-initial-position" });
		this.#titlePara = node("p", { className: "settings-initial-position__paragraph" });

		const header = createHeader("initial-position", _("profile.settings.position.title"));

		this.#container.appendChild(header);

		// info o prazdne nebo vyplnene souradnici
		const iconButton = node("button", { className: "settings-initial-position__menu-button", type: "button" });
		const menu = fragment();
		const editMenuItem = new ContextMenuItem({
			icon: createSDSIcon({
				symbol: EDIT_FILLED_24,
			}),
			text: _("edit"),
			onClick: () => {
				this.#editStart();
				this.#contextMenu.close();
			},
		});
		const showOnMapMenuItem = new ContextMenuItem({
			icon: createSDSIcon({
				symbol: POI_FILLED_24,
			}),
			text: _("showOnMap"),
			onClick: () => {
				this.#props.mapCoords && store.dispatch(setCoordinates({
					longitude: this.#props.mapCoords.mark.lon,
					latitude: this.#props.mapCoords.mark.lat,
					zoom: this.#props.mapCoords.zoom,
					animate: true,
				}));
				this.#contextMenu.close();
			},
		});
		const deleteMenuItem = new ContextMenuItem({
			icon: createSDSIcon({
				symbol: TRASH_FILLED_24,
			}),
			text: _("delete"),
			onClick: () => {
				Mapy.getComponent("logger").log("position_default_reset", {
					section: "accounts",
				});
				this.#newPosition = null;
				this.#props.onChange && this.#props.onChange(null);
				this.#contextMenu.close();
			},
		});

		iconButton.appendChild(icon("etc-vertical"));
		iconButton.setAttribute("data-log-ignore", "1");

		editMenuItem.container.setAttribute("data-log-section", "accounts");
		menu.appendChild(editMenuItem.container);

		showOnMapMenuItem.container.setAttribute("data-log-section", "accounts");
		menu.appendChild(showOnMapMenuItem.container);

		deleteMenuItem.container.setAttribute("data-log-section", "accounts");
		menu.appendChild(deleteMenuItem.container);

		this.#contextMenu = new ContextMenu({
			anchor: iconButton,
			content: menu,
			flow: EPopOverFlow.END,
		});
		iconButton.addEventListener("click", () => {
			this.#contextMenu.toggle();
		});

		header.appendChild(iconButton);

		const emptyButton = createLinkButton({ innerHTML: _("set") });
		const saveButton = createDialogButton({ innerHTML: _("save"), primary: true });

		emptyButton.setAttribute("data-log-section", "accounts");
		emptyButton.addEventListener("click", () => {
			this.#editStart();
		});

		header.appendChild(emptyButton);

		this.#container.appendChild(this.#titlePara);

		// formular pro editaci
		this.#form = node("form", { className: "settings-initial-position__form" });

		this.#form.appendChild(node("p", { className: "settings-initial-position__paragraph", innerHTML: _("profile.settings.position.instructions") }));

		let div = node("div");

		this.#input = node("input", { type: "text", className: "settings-initial-position__input" });
		this.#input.addEventListener("input", () => {
			this.#newPosition = null;
			saveButton.disabled = this.#input.value !== "";
		}, false);
		this.#suggest = new InputSuggestMapClick({
			input: this.#input,
			onLoadingStart: () => {
				this.loading = true;
			},
			onLoadingEnd: () => {
				this.loading = false;
			},
			onSelect: (poi: any) => {
				const coords = Scene.stringToCoords(poi.geometry);

				if (coords.length) {
					const [lon, lat] = coords[0].toWGS84();
					const { zoom } = store.getState().map;

					this.#newPosition = {
						mark: {
							lon,
							lat,
						},
						source: poi.source,
						// eslint-disable-next-line camelcase
						source_id: poi.id,
						title: poi.title,
						// prepise se stejne pri ulozeni, ale radeji mame validni zoom uz ted
						zoom,
					};
					saveButton.disabled = false;
					this.#input.value = poi.title;

					const [longitude, latitude] = coords[0].toWGS84();

					store.dispatch(setCoordinates({
						longitude,
						latitude,
						animate: true,
					}));
				}
			},
		});
		div.appendChild(this.#input);
		this.#form.appendChild(div);

		// kontajner na tlacitka a loader
		div = node("div");

		saveButton.addEventListener("click", async () => {
			const { zoom } = store.getState().map;
			const position: IUserInitialPosition = this.#newPosition ? {
				...this.#newPosition,
				zoom,
			} : null;

			this.loading = true;

			if (!sameCoords(position, this.#props.mapCoords)) {
				Mapy.getComponent("logger").log(position
					? this.#props.mapCoords === null && position !== null
						? "position_default_set"
						: "position_default_edit"
					: "position_default_reset", {
					section: "accounts",
				});
			}

			this.#props.onChange && await this.#props.onChange(position);
			this.loading = false;
			this.#editStop();
		}, false);
		saveButton.setAttribute("data-log-section", "accounts");
		div.appendChild(saveButton);

		// tlacitko zrusit
		const cancelButton = createDialogButton({ innerHTML: _("cancel") });

		cancelButton.addEventListener("click", () => {
			this.#editStop();
		}, false);
		cancelButton.setAttribute("data-log-section", "accounts");
		div.appendChild(cancelButton);

		// loader
		const loader = node("img", { src: "/img/loader.gif", alt: "", className: "settings-initial-position__loader" });

		loader.setAttribute("role", "presentation");
		div.appendChild(loader);
		this.#form.appendChild(div);

		this.#container.appendChild(this.#form);
	}

	#editStart() {
		this.#container.classList.add("settings-initial-position--editmode");
		this.#input.focus();
	}

	#editStop() {
		this.#container.classList.remove("settings-initial-position--editmode");
	}
}
